// ** React Imports
import React from 'react'

// ** Redux Imports
import { useSelector } from 'react-redux'
import { customersSelector } from 'store/customers'

// ** MUI Imports
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'

// ** 3rd Party Libraries
import moment from 'moment'
import { currencySelector } from 'store/metaData/currency'

const currencyFormatter = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const TransactionsTableRow = props => {
  // ** Props
  const { row, type } = props

  // ** Selectors
  const { customerInformation, invoiceInformation } = useSelector(customersSelector)
  const { currenciesData } = useSelector(currencySelector)

  // ** Constants
  const currency = customerInformation?.currency?.id
    ? customerInformation?.currency
    : currenciesData?.find(item => item.iso_code === 'CAD')

  // Calculate single row
  const calculateSingleRow = (prices, total, row) => {
    const customerCurrencyId = currency?.id
    const currencySymbol = currency?.iso_code === 'EUR' ? '€' : '$'
    const price = prices?.find(item => item.currencyId === customerCurrencyId)?.totalValue ?? 0
    if (type === 'advance')
      return total < 0 ? '-' + currencySymbol + ((total || 0) * -1)?.toFixed(2) : currencySymbol + total?.toFixed(2)
    if (price < 0) return '-' + currencySymbol + ((price || 0) * -1)?.toFixed(2)
    else return currencySymbol + (price || 0)?.toFixed(2)
  }

  // Find domain
  const findDomain = id => {
    const domain =
      customerInformation?.googleIds?.find(el => el.subscriptions?.some(sub => sub.subscriptionId === id))?.domain ??
      customerInformation?.domain
    return domain
  }

  // Format date
  const formatDate = (intervals, type) => {
    if (!intervals || !intervals.length) return [] // Handle empty input

    if (type === 'advance') {
      const start = moment(intervals?.[intervals?.length - 1]?.from, 'MM/DD/YYYY', true)
      const end = moment(intervals?.[0]?.to, 'MM/DD/YYYY', true)

      if (!start.isValid() || !end.isValid()) return 'Invalid date'

      if (start.isSame(end, 'day')) {
        return [start.format('MMM DD, YYYY')]
      } else if (start.year() !== end.year()) {
        return [`${start.format('MMM DD, YYYY')} - ${end.format('MMM DD, YYYY')}`]
      } else if (start.month() === end.month()) {
        return [`${start.format('MMM DD')} - ${end.format('DD, YYYY')}`]
      } else {
        return [`${start.format('MMM DD')} - ${end.format('MMM DD, YYYY')}`]
      }
    }

    return intervals.map(period => {
      const start = moment(period.from, 'MM/DD/YYYY', true)
      const end = moment(period.to, 'MM/DD/YYYY', true)

      if (!start.isValid() || !end.isValid()) return 'Invalid date'

      if (start.year() !== end.year()) {
        return `${start.format('MMM DD, YYYY')} - ${end.format('MMM DD, YYYY')}`
      } else if (start.isSame(end, 'day')) {
        return start.format('MMM DD, YYYY')
      } else if (start.month() === end.month()) {
        return `${start.format('MMM DD')} - ${end.format('DD, YYYY')}`
      } else {
        return `${start.format('MMM DD')} - ${end.format('MMM DD, YYYY')}`
      }
    })
  }

  // Format discount date
  const formatDiscountDate = (discount, subscriptionId, licenceType, discounts, intervals, unitPrice, totalBeforeDiscount) => {
    const totalDiscounts = discounts?.reduce((acc, { value }) => acc + parseFloat(value || 0), 0)
    let subscriptionPrice = null
    let period = null
    let subscriptionData = null
    const licenceTypeFormatted = licenceType === 'FREE' ? '' : licenceType === 'FLEXIBLE' ? 'flexible' : 'annual'
    if (!!subscriptionId) {
      customerInformation?.googleIds?.forEach(googleId => {
        const subscription = googleId?.subscriptions?.find(sub => sub?.subscriptionId === subscriptionId) ?? null
        subscriptionData = googleId?.subscriptions?.find(sub => sub?.subscriptionId === subscriptionId) ?? null
        if (!!subscription) {
          const customerPricingBook = customerInformation?.subscriptionPricingBooks?.find(
            item =>
              item.subscriptionId === subscription?.subscriptionId &&
              moment(intervals[0]?.from).isSameOrAfter(moment(item?.startDate).format('YYYY-MM-DD')) &&
              moment(intervals[0]?.to).isSameOrBefore(moment(item?.endDate).format('YYYY-MM-DD'))
          )
          if (!!customerPricingBook?.pricingBookId) {
            const subscriptionEntity = subscription?.product?.prices?.find(
              item =>
                item.currencyId === currency?.id &&
                item.type === licenceTypeFormatted &&
                item.pricingBookId === customerPricingBook?.pricingBookId &&
                moment(intervals[0]?.from || invoiceInformation?.issue_date).isSameOrAfter(
                  moment(customerPricingBook?.startDate).format('YYYY-MM-DD')
                ) &&
                moment(intervals[0]?.to || invoiceInformation?.due_date).isSameOrBefore(
                  moment(customerPricingBook?.endDate).format('YYYY-MM-DD')
                )
            )

            if (subscriptionEntity?.id) {
              subscriptionPrice = subscriptionEntity?.value
            } else {
              const defaultPricing = subscription?.product?.prices?.find(
                item =>
                  item.currencyId === currency?.id && item.type === licenceTypeFormatted && item.pricingBook?.is_default
              )
              subscriptionPrice = defaultPricing?.value || 0
            }
          } else {
            const subscriptionEntity = subscription?.product?.prices?.find(
              item =>
                item.currencyId === currency?.id && item.type === licenceTypeFormatted && item.pricingBook?.is_default
            )
            subscriptionPrice = subscriptionEntity?.value || 0
          }
        }
      })
    }

    const startDate = moment(discount?.startDate, 'YYYY-MM-DD')
    const endDate = moment(discount?.endDate, 'YYYY-MM-DD')

    const days = endDate.diff(startDate, 'days')

    if (days <= 30) {
      period = days > 1 ? days + ' days' : days + ' day'
    } else if (days < 365 && days > 30) {
      const months = days / 30
      period = `${parseInt(months)} ${parseInt(months) > 1 ? 'months' : 'month'}`
    } else if (days >= 365) {
      const years = days / 365
      const restDaysFromYears = days % 356
      if (restDaysFromYears > 30) {
        period = `${parseInt(years)} ${parseInt(years) > 1 ? 'years' : 'year'}`
      } else if (restDaysFromYears < 30 && restDaysFromYears > 0) {
        period = `${parseInt(years)} ${parseInt(years) > 1 ? 'years' : 'year'}`
      }
    }
    const discountText =
      totalDiscounts > 0
        ? 'Discount: ' +
          (Number.isInteger(totalDiscounts) ? totalDiscounts.toString() : totalDiscounts.toFixed(2)) +
          '% for ' +
          period
        : ''

    const beforeDiscountText =
      discountText && totalBeforeDiscount
        ? ' (Cost before discount: ' +
          (currency?.iso_code === 'EUR' ? '€' : '$') +
          currencyFormatter.format(totalBeforeDiscount) +
          ')'
        : ''

    let priceText = ''
    if (!(totalDiscounts > 0) && licenceTypeFormatted == 'annual') {
      let duration = '1 year'
      if (subscriptionData?.startTime && subscriptionData?.endTime) {
        let differenceInYears = moment(subscriptionData.endTime).diff(subscriptionData.startTime, 'years')

        if (differenceInYears > 1) duration = differenceInYears + ' years'
      }
      priceText = 'List Price for ' + duration
    }
    let listPriceTex = ''
    if (subscriptionPrice && licenceTypeFormatted !== 'flexible')
      listPriceTex =
        (discountText ? ' (' : '(') +
        'List price: ' +
        (currency?.iso_code === 'EUR' ? '€' : '$') +
        (subscriptionPrice || 0) +
        '/user/month)'
    if (subscriptionPrice && licenceTypeFormatted == 'flexible')
      listPriceTex =
        '(Flexible price: ' + (currency?.iso_code === 'EUR' ? '€' : '$') + (subscriptionPrice || 0) + '/user/month)'
    if (subscriptionPrice && priceText)
      listPriceTex =
        priceText +
        ' (' +
        (currency?.iso_code === 'EUR' ? '€' : '$') +
        ((Number.isInteger(subscriptionPrice) ? subscriptionPrice?.toString() : subscriptionPrice?.toFixed(2)) || 0) +
        '/user/month)'
    if (unitPrice)
      listPriceTex =
        (discountText ? ' (' : '(') +
        'List price: ' +
        (currency?.iso_code === 'EUR' ? '€' : '$') +
        (Number.isInteger(unitPrice) ? unitPrice.toString() : unitPrice.toFixed(2)) +
        '/user/month)'
    return discountText + beforeDiscountText + listPriceTex
  }

  //  Format cover period
  const coverPeriod = (item, intervals, itemType) => {
    const extra_text_description =
      item?.unit == 'Minute' && item?.seats > 0 ? item?.resource_type + ' (' + item?.seats + ' minutes long)' : ''
    if (type === 'advance') {
      if (itemType === 'change') {
        if (item?.seats > 0) {
          return (
            <>
              {'Prorated Charge for ' + item?.seats + ' additional license' + (item?.seats > 1 ? 's' : '')}
              {' - ' + item.maxCumulatedChangeSeat + ' total'}
              <br />
              {'Charge to cover: '}
              {intervals?.length > 0 &&
                (() => {
                  const firstInterval = intervals[0]
                  const lastInterval = intervals[intervals?.length - 1]
                  const fromDate = moment(lastInterval?.from).format('MM/DD/YYYY')
                  const toDate = moment(firstInterval?.to).format('MM/DD/YYYY')
                  return fromDate === toDate ? fromDate : `${fromDate} - ${toDate}`
                })()}
              {extra_text_description ? <br /> : null}
              {extra_text_description}
            </>
          )
        } else {
          return (
            <>
              {'Credit for license decrease' + ' - ' + item.maxCumulatedChangeSeat + ' total'}
              <br />
              {'Covers: '}
              {intervals?.length > 0 &&
                (() => {
                  const firstInterval = intervals[0]
                  const lastInterval = intervals[intervals?.length - 1]
                  const fromDate = moment(lastInterval?.from).format('MM/DD/YYYY')
                  const toDate = moment(firstInterval?.to).format('MM/DD/YYYY')
                  return fromDate === toDate ? fromDate : `${fromDate} - ${toDate}`
                })()}
            </>
          )
        }
      }
      return (
        <>
          {'Charge to cover: '}
          {intervals?.length > 0 &&
            (() => {
              const firstInterval = intervals[0]
              const lastInterval = intervals[intervals?.length - 1]
              const fromDate = moment(lastInterval?.from).format('MM/DD/YYYY')
              const toDate = moment(firstInterval?.to).format('MM/DD/YYYY')
              return fromDate === toDate ? fromDate : `${fromDate} - ${toDate}`
            })()}
          {extra_text_description ? <br /> : null}
          {extra_text_description}
        </>
      )
    }
    return (
      <>
        {'Charge to cover: '}
        {intervals?.length > 0 &&
          (() => {
            const lastInterval = intervals[intervals?.length - 1]
            const fromDate = moment(lastInterval?.from).format('MM/DD/YYYY')
            const toDate = moment(lastInterval?.to).format('MM/DD/YYYY')
            return fromDate === toDate ? fromDate : `${fromDate} - ${toDate}`
          })()}
        {extra_text_description ? <br /> : null}
        {extra_text_description}
      </>
    )
  }

  // Finc Product
  const workSpaceProduct = subscription_id => {
    return customerInformation?.googleIds?.some(googleId => {
      const product = googleId?.subscriptions?.find(sub => sub.subscriptionId === subscription_id)?.product
      return !product?.is_isv_product && !product?.is_premiercloud_product
    })
  }

  const formatSkuName = item => {
    let name = item.sku_name.includes('Google Workspace')
      ? 'Google Workspace License -' + item.sku_name.replace('Google Workspace', '')
      : item.sku_name
    if (item.sku_name?.toLowerCase() === 'google voice')
      name = item?.unit != 'Minute' ? item?.resource_type : 'Long distance charge'
    return name
  }

  return (
    <TableRow sx={{ '&:last-of-type td, &:last-of-type th': { border: 0 } }}>
      <TableCell component='th' scope='row'>
        {formatDate(row.intervals)?.join(' - ') ?? ''}
      </TableCell>
      <TableCell>
        {workSpaceProduct(row.subscription_id) ? formatSkuName(row) : row.sku_name}
        <br />
        {'Domain: ' + findDomain(row.subscription_id)}
        <br />
        {row.type !== 'billedUsage' && coverPeriod(row, row.intervals, row.type)}
        {row.type !== 'billedUsage' ? <br /> : null}
        {row.type !== 'billedUsage'
          ? formatDiscountDate(
              row.discount?.[0],
              row.subscription_id,
              row?.licence_type,
              row.discount,
              row.intervals,
              row.list_price,
              row.total_before_discount
            )
          : null}
        {row.type === 'billedUsage' && 'Prorated credit for amount already paid'}
        {row.type === 'billedUsage' ? <br /> : null}
        {row.type === 'billedUsage' && row.label}
      </TableCell>
      <TableCell align='right'>{row.seats < 0 ? row.seats * -1 : row.seats}</TableCell>
      <TableCell align='right'>{calculateSingleRow(row.prices, row.total, row)}</TableCell>
    </TableRow>
  )
}

export default React.memo(TransactionsTableRow)
